import {
	ICrossWindowApi,
	ICrossWindowApiOptions,
	ICrossWindowCancelable,
	ICrossWindowFactory,
	ICrossWindowPeerInfo,
} from "@AppBuilderLib/shared/config/crosswindowapi/crosswindowapi";
import postRobot from "post-robot";

type CrossDomainWindowType = Window | null;
type HandlerType = (event: {
	source: CrossDomainWindowType;
	origin: string;
	data: any;
}) => Promise<any>;

const MESSAGE_TYPE_READY = "API_READY";

/**
 * The interval in which the peer is notified that we are ready.
 * The shorter the interval, the faster the communication is established (but more messages are sent).
 */
const SETUP_INTERVAL = 100;

class CrossWindowApi implements ICrossWindowApi {
	private window: Window;
	private debug: boolean;
	private timeout?: number;
	public name: string;
	public peerName: string;
	public peerIsReady: Promise<ICrossWindowPeerInfo>;
	private handshakeIntervalId?: ReturnType<typeof setInterval>;
	private handshakeTimeoutId?: ReturnType<typeof setTimeout>;
	private handshakeToken?: ICrossWindowCancelable;
	private handshakeReject?: (reason: Error) => void;

	constructor(
		window: Window,
		name: string,
		peerName: string,
		options?: ICrossWindowApiOptions,
	) {
		if (window === globalThis.window) {
			const msg =
				"Cannot create an API for self (window === global.window)";
			this.log(msg);
			throw new Error(msg);
		}

		this.window = window;
		this.debug = options?.debug ?? false;
		this.timeout = options?.timeout;
		this.name = name;
		this.peerName = peerName;

		// post-robot send() to a popup/iframe waits onChildWindowReady using
		// the same timeout. A 100ms timeout rejects that promise and caches it,
		// so every later parent→child send fails (ToolsApi handshake too).
		// Plant a long wait first (sync), then retry ACK on SETUP_INTERVAL.
		const pingTimeout = this.timeout ?? 5000;
		let intervalId: ReturnType<typeof setInterval> | undefined;
		const onPingSuccess = (result: unknown) => {
			this.log(
				"Peer answered ready event",
				(result as {origin?: string})?.origin,
			);
			if (intervalId !== undefined) {
				clearInterval(intervalId);
				intervalId = undefined;
			}
		};

		void postRobot
			.send(
				window,
				`${this.name}${MESSAGE_TYPE_READY}`,
				{name},
				{timeout: pingTimeout},
			)
			.then(onPingSuccess)
			.catch((error: unknown) => {
				this.log(
					"Peer not ready (initial ping)",
					error instanceof Error ? error.message : error,
				);
			});

		intervalId = setInterval(async () => {
			try {
				const result = await postRobot.send(
					window,
					`${this.name}${MESSAGE_TYPE_READY}`,
					{name},
					{timeout: SETUP_INTERVAL},
				);
				onPingSuccess(result);
			} catch (error) {
				this.log(
					"Peer not ready",
					error instanceof Error ? error.message : error,
				);
			}
		}, SETUP_INTERVAL);

		// wait until the peer is ready
		this.peerIsReady = new Promise<ICrossWindowPeerInfo>(
			(resolve, reject) => {
				const timeoutId = this.timeout
					? setTimeout(() => {
							const msg = `Peer did not respond within ${this.timeout}ms, giving up`;
							this.log(msg);
							token.cancel();
							clearInterval(intervalId);
							reject(new Error(msg));
						}, this.timeout)
					: undefined;

				const token = postRobot.on(
					`${this.peerName}${MESSAGE_TYPE_READY}`,
					{window},
					async (event) => {
						if (event.data.name !== this.peerName) {
							const msg = `Peer ready event: name mismatch: ${event.data.name} !== ${this.peerName}`;
							this.log(msg);
							throw new Error(msg);
						}
						this.log(
							"Peer ready event received",
							event.origin,
							event.data?.name,
						);
						clearTimeout(timeoutId);
						token.cancel();
						resolve({
							window: event.source,
							origin: event.origin,
							name: event.data.name as unknown as string,
						});

						return {name: this.name};
					},
				);
			},
		);
	}

	handshake(type: string, timeout?: number): Promise<ICrossWindowPeerInfo> {
		return new Promise<ICrossWindowPeerInfo>((resolve, reject) => {
			let settled = false;
			const complete = () => {
				if (settled) {
					return;
				}
				settled = true;
				this.clearHandshakeResources();
				resolve(this.peerIsReady);
			};

			this.handshakeReject = (reason) => {
				if (settled) {
					return;
				}
				settled = true;
				reject(reason);
			};
			this.handshakeTimeoutId = timeout
				? setTimeout(() => {
						const msg = `Peer did not respond to handshake "${type}" within ${timeout}ms, giving up`;
						this.log(msg);
						this.handshakeReject?.(new Error(msg));
						this.clearHandshakeResources();
					}, timeout)
				: undefined;

			this.handshakeToken = this.on(type, async () => {
				this.log(`Handshake "${type}" successfully received`);
				complete();
			});

			this.handshakeIntervalId = setInterval(async () => {
				try {
					await this.send(type, undefined);
					this.log(`Handshake "${type}" successfully sent`);
					complete();
				} catch {
					// ignore
				}
			}, SETUP_INTERVAL);
		});
	}

	/** Stop handshake() polling and the handshake listener. No-op if handshake was not started or already finished. */
	cancelHandshake(): void {
		const reject = this.handshakeReject;
		this.clearHandshakeResources();
		reject?.(new Error("Handshake cancelled"));
	}

	private clearHandshakeResources(): void {
		if (this.handshakeIntervalId !== undefined) {
			clearInterval(this.handshakeIntervalId);
			this.handshakeIntervalId = undefined;
		}
		if (this.handshakeTimeoutId !== undefined) {
			clearTimeout(this.handshakeTimeoutId);
			this.handshakeTimeoutId = undefined;
		}
		this.handshakeToken?.cancel();
		this.handshakeToken = undefined;
		this.handshakeReject = undefined;
	}

	log(...message: any[]): void {
		if (!this.debug) {
			return;
		}
		try {
			console.log(
				`CrossWindowApi (name = "${this.name}", peerName = "${this.peerName}"):`,
				...message,
			);
		} catch {
			// console.log of a cross-origin Window (event.source) throws
			// SecurityError and must not abort API_READY / handshake handlers.
		}
	}

	async once<Trequest, Tresponse>(
		type: string,
		_handler: (data: Trequest) => Promise<Tresponse>,
	): Promise<Trequest> {
		await this.peerIsReady;
		const handler: HandlerType = (event) =>
			_handler(event.data as Trequest);
		const requestEvent = await postRobot.once(`${this.peerName}:${type}`, {
			handler,
			window: this.window,
		});

		return requestEvent.data as Trequest;
	}

	on<Trequest, Tresponse>(
		type: string,
		_handler: (data: Trequest) => Promise<Tresponse>,
	): ICrossWindowCancelable {
		const handler: HandlerType = (event) =>
			_handler(event.data as Trequest);

		return postRobot.on(`${this.peerName}:${type}`, {
			handler,
			window: this.window,
		});
	}

	async send<Trequest extends object | undefined, Tresponse>(
		type: string,
		data: Trequest,
		timeout?: number,
	): Promise<Tresponse> {
		await this.peerIsReady;

		return postRobot
			.send(this.window, `${this.name}:${type}`, data, {
				timeout: timeout ?? this.timeout,
			})
			.then((response) => {
				return response.data as Tresponse;
			});
	}
}

class _CrossWindowApiFactory implements ICrossWindowFactory {
	async getParentApi(
		name: string,
		peerName: string,
		options?: ICrossWindowApiOptions,
	): Promise<ICrossWindowApi> {
		return this.getWindowApi(window.parent, name, peerName, options);
	}

	async getWindowApi(
		window: Window,
		name: string,
		peerName: string,
		options?: ICrossWindowApiOptions,
	): Promise<ICrossWindowApi> {
		const api = new CrossWindowApi(window, name, peerName, options);
		await api.peerIsReady;

		return api;
	}
}

export const CrossWindowApiFactory = new _CrossWindowApiFactory();
